/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import { FC, useEffect, useMemo, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import { SubjectTabs } from 'components/SubjectsTabs'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { usePlatformsAndSubjects, Props as UsePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { FIND_ALL_STRATEGY_GROUP } from 'queries/strategies'
import {
  findAllStrategyGroup,
  findAllStrategyGroupVariables,
  findAllStrategyGroup_findAllStrategyGroup_items as Group,
} from '__generatedTypes__/findAllStrategyGroup'
import Loader from 'components/Loader'
import * as classes from 'pagesNew/FlashCards/styles'
import { StrategiesGroups } from './components/StrategiesGroups'
import { CARD_GROUP_QUERY_PARAM, GroupCards } from './components/GroupCards'

export const PAT_SUBJECTS = [
  'keyholes',
  'top-front-end',
  'angle-ranking',
  'hole-punching',
  'cube-counting',
  'pattern-folding',
]

const SUBJECTS_SLUGS = ['perceptual-ability', 'reading-comprehension', ...PAT_SUBJECTS]
export const QUERY_PARAM = 'subject-tab'

export const List: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')

  const platformsAndSubjectsProps: UsePlatformsAndSubjects = useMemo(
    () => ({
      initialSelectedSubjectId: subjectId || null,
      autoSelectFirstSubject: true,
      filterSubjects: (subject) => SUBJECTS_SLUGS.includes(subject.slug),
    }),
    [subjectId]
  )

  const {
    subjects,
    selectedSubject,
    selectSubject,
    loading: loadingSubjects,
  } = usePlatformsAndSubjects(platformsAndSubjectsProps)

  const [groups, setGroups] = useState<Group[]>([])

  const [getGroups, { loading: loadingGroups }] = useLazyQuery<findAllStrategyGroup, findAllStrategyGroupVariables>(
    FIND_ALL_STRATEGY_GROUP,
    {
      onCompleted: (data) => {
        setGroups(data?.findAllStrategyGroup.items || [])
      },
    }
  )

  useEffect(() => {
    if (selectedSubject) {
      setSearchParams((prev) => ({
        ...Object.fromEntries(prev.entries()),
        subjectId: selectedSubject.id,
        [QUERY_PARAM]: selectedSubject.slug,
      }))

      getGroups({
        variables: {
          query: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              subject: {
                id: selectedSubject.id,
              },
            },
          },
        },
      })
    }
  }, [selectedSubject?.id])

  const initialSelectedGroup = useMemo(() => {
    const selectedGroupId = searchParams.get(CARD_GROUP_QUERY_PARAM)

    if (selectedGroupId) {
      return groups.find(({ id }) => id === selectedGroupId) || groups[0]
    }
    return groups[0]
  }, [groups])

  if (loadingSubjects) {
    return <Loader />
  }

  if (!selectedSubject) {
    return null
  }

  return (
    <>
      <SubjectTabs subjects={subjects} selectedSubject={selectedSubject} selectSubject={selectSubject} />
      {loadingGroups ? (
        <Loader />
      ) : (
        <>
          <StrategiesGroups groups={groups} selectedSubject={selectedSubject} setGroups={setGroups} />
          {!!groups?.length && (
            <GroupCards groups={groups} selectedSubjectId={selectedSubject.id} initialSelected={initialSelectedGroup} />
          )}
        </>
      )}
    </>
  )
}

export const StrategiesList: FC = () => (
  <div css={classes.container}>
    <Typography css={classes.header} variant="title3">
      Strategies
    </Typography>
    <List />
  </div>
)
